const DEFAULT_FALLBACK = 'attachment';

export type SlugifySegmentOptions = {
  fallback?: string;
  maxLength?: number;
  separator?: string;
};

/**
 * Normalizes a string into a URL/path-safe segment.
 * Strips diacritics, converts to kebab-case, and falls back when empty.
 */
export function slugifySegment(value: string | null | undefined, options?: SlugifySegmentOptions): string {
  const fallback = options?.fallback ?? DEFAULT_FALLBACK;
  const maxLength = options?.maxLength ?? null;
  const separator = options?.separator ?? '-';

  let normalized = String(value ?? '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  if (separator !== '-') {
    normalized = normalized.replace(/-/g, separator);
  }

  const trimmed = maxLength != null && maxLength > 0 ? normalized.slice(0, maxLength) : normalized;

  return trimmed || fallback;
}

export type TruncateTextOptions = {
  suffix?: string;
  preserveWords?: boolean;
  stripTrailingPunctuation?: boolean;
  includeRemaining?: boolean;
  normalizeWhitespace?: boolean;
  fallback?: string;
};

const DEFAULT_TRUNCATE_SUFFIX = '…';
const TRAILING_PUNCTUATION_REGEX = /[.,;:!\-–—]+$/u;

/**
 * Shared text truncation helper with support for ellipsis, word preservation,
 * whitespace normalisation, and optional overflow metadata.
 */
export function truncateText(
  value: string | null | undefined,
  limit: number,
  options?: TruncateTextOptions,
): string | null | undefined {
  if (value == null) {
    return options?.fallback ?? value;
  }

  const maxLength = Math.max(0, limit);
  let text = String(value);

  if (options?.normalizeWhitespace) {
    text = text.replace(/\s+/g, ' ').trim();
  }

  if (!text) {
    return options?.fallback ?? text;
  }

  if (text.length <= maxLength) {
    return text;
  }

  let truncated = text.slice(0, maxLength);

  if (options?.preserveWords) {
    const lastSpace = truncated.lastIndexOf(' ');
    if (lastSpace > 0) {
      truncated = truncated.slice(0, lastSpace);
    }
    if (!truncated) {
      truncated = text.slice(0, maxLength);
    }
  }

  truncated = truncated.trimEnd();

  if (options?.stripTrailingPunctuation) {
    const withoutPunctuation = truncated.replace(TRAILING_PUNCTUATION_REGEX, '').trimEnd();
    truncated = withoutPunctuation || truncated;
  }

  const remaining = text.length - truncated.length;
  const suffix = options?.suffix ?? DEFAULT_TRUNCATE_SUFFIX;

  let result = truncated;
  if (suffix) {
    result += suffix;
  }

  if (options?.includeRemaining && remaining > 0) {
    result += ` (+${remaining} chars)`;
  }

  return result;
}

/**
 * Converts a string to title case (capitalizes first letter of each word).
 * Handles empty strings, multiple spaces, and preserves special characters.
 */
export function toTitleCase(value: string): string {
  if (!value || !value.trim()) return value;
  
  return value
    .split(/\s+/)
    .map(word => {
      if (!word) return word;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

export function snakeToTitleCase(value: string): string {
  return toTitleCase(value.replace(/_/g, ' '));
}

/**
 * Create a preview of text with ellipsis.
 * @param text - Text to preview
 * @param maxLength - Maximum length for preview (default: 100)
 * @returns Preview text with ellipsis if truncated
 */
export function createPreview(text: string, maxLength: number = 100): string {
  const result = truncateText(text, maxLength, { suffix: '...' });
  return result ?? '';
}

/**
 * Create a short preview of text (50 chars).
 * @param text - Text to preview
 * @returns Short preview text with ellipsis if truncated
 */
export function createShortPreview(text: string): string {
  return createPreview(text, 50);
}
